import React, { useState } from 'react';
import { Modal, Button, Typography, Space } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { deleteDoc, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../../data/base';
import { secureGetItem } from '../../../data/utils';

const { Text } = Typography;

/**
 * Modal shown when a user tries to modify a published catalog that already has a draft
 * Provides options to continue with the existing draft, create a new draft, or cancel
 */
const ExistingDraftModal = ({
  isOpen,
  onClose,
  publishedCatalogueId,
  draftedCatalogRef,
  onContinueWithDraft,
  onCreateNewDraft
}) => {
  // Function to handle continuing with the existing draft
  const handleContinueWithDraft = () => {
    onContinueWithDraft(draftedCatalogRef);
    onClose();
  };

  // State for confirmation modal
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);

  // Function to show confirmation modal
  const showConfirmModal = () => {
    // Close the first modal and open the confirmation modal
    onClose();
    setConfirmModalVisible(true);
  };

  // Function to handle creating a new draft (deleting the existing one)
  const handleCreateNewDraft = async () => {
    // Close the confirmation modal
    setConfirmModalVisible(false);
    try {

      // 1. Delete the reference from the published catalog
      const publishedCatalogRef = doc(db, 'Catalogues', publishedCatalogueId);
      await updateDoc(publishedCatalogRef, {
        draftedCatalogRef: null
      });

      // 2. Remove the draft ID from the user's profile
      const userEmail = secureGetItem('email');
      if (userEmail) {
        const profileRef = doc(db, 'Profiles', userEmail);
        const profileSnap = await getDoc(profileRef);

        if (profileSnap.exists()) {
          const profileData = profileSnap.data();
          const draftsList = profileData.list_drafted_catalogues || [];

          // Filter out the draft ID
          const updatedDraftsList = draftsList.filter(id => id !== draftedCatalogRef);

          // Update the profile
          await updateDoc(profileRef, {
            list_drafted_catalogues: updatedDraftsList
          });

        }
      }

      // 3. Delete the draft document
      const draftRef = doc(db, 'DraftedCatalogues', draftedCatalogRef);
      await deleteDoc(draftRef);

      // 4. Call the callback to create a new draft
      onCreateNewDraft();
      onClose();
    } catch (error) {
      // Still proceed with creating a new draft even if deletion fails
      onCreateNewDraft();
      onClose();
    }
  };

  return (
    <>
      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: '#faad14' }} />
            <span>Existing Draft Found</span>
          </Space>
        }
        open={isOpen}
        onCancel={onClose}
        footer={null}
        centered
      >
        <div style={{ marginBottom: 24 }}>
          <Text>
            Warning: You already have pending changes in a draft catalog.
            Would you like to continue modifying that draft or create a new one?
            If you create a new one, the pending changes will be deleted.
          </Text>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={showConfirmModal}
            danger
          >
            Create New Version
          </Button>
          <Button
            type="primary"
            onClick={handleContinueWithDraft}
          >
            Continue with Modifications
          </Button>
        </div>
      </Modal>

      {/* Confirmation Modal - Shown separately from the first modal */}
      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
            <span>Confirm Action</span>
          </Space>
        }
        open={confirmModalVisible}
        onCancel={() => {
          setConfirmModalVisible(false);
          // Don't reopen the first modal when this one is cancelled
        }}
        footer={null}
        centered
      >
        <div style={{ marginBottom: 24 }}>
          <Text>
            Are you sure you want to continue? Current modifications will be lost and cannot be recovered.
          </Text>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={() => setConfirmModalVisible(false)}>
            Cancel
          </Button>
          <Button
            type="primary"
            danger
            onClick={handleCreateNewDraft}
          >
            Yes
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default ExistingDraftModal;
